import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { PiCaretLeftBold } from "react-icons/pi";
import { StatusDot } from "../components/Bits";
import { TICKETS, SEVERITY_META, TICKET_STATUS_META } from "../data/mockData";

/**
 * Ticket detail with the automated diagnostic trace.
 *
 * The trace is a numbered rail: each stage is a 22px node joined by a hairline,
 * carrying monospace text, an optional confidence meta line, an optional list of
 * automated checks, and — on the final stage — the suggested-fix actions.
 * Approve / Reject / Investigate are local-only and swap themselves for a tag,
 * matching the prototype.
 */
const TicketDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [action, setAction] = useState(null);

  const ticket = TICKETS.find((t) => t.id === id);

  if (!ticket) {
    return (
      <div className="admin-stub">
        <div className="admin-stub-mark">?</div>
        <h4 style={{ margin: 0 }}>Ticket not found</h4>
        <p className="card-body" style={{ flex: "none" }}>No ticket matches the id “{id}”.</p>
        <button type="button" className="btn btn-secondary" onClick={() => navigate("/admin/tickets")}>
          Back to tickets
        </button>
      </div>
    );
  }

  const sev = SEVERITY_META[ticket.severity];
  const st = TICKET_STATUS_META[ticket.status];

  return (
    <>
      <button
        type="button"
        className="btn btn-ghost"
        onClick={() => navigate("/admin/tickets")}
        style={{ marginBottom: 14 }}
      >
        <PiCaretLeftBold /> Back to tickets
      </button>

      <div className="admin-split">
        <div className="card">
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <StatusDot color={sev.color} label={sev.label} />
            <span className="tag tag-neutral">{ticket.category}</span>
            <span className={`tag ${st.tagClass}`}>{st.label}</span>
          </div>

          <h3 style={{ margin: "8px 0 2px" }}>{ticket.subject}</h3>
          <div className="admin-drawer-meta" style={{ marginBottom: 10 }}>
            {ticket.id} · {ticket.requester} · opened {ticket.created}
          </div>
          <p style={{ fontSize: 13, opacity: 0.85 }}>{ticket.snippet}</p>

          <div className="hr" />

          <h5 style={{ margin: "0 0 14px" }}>Diagnostic trace</h5>

          <div className="admin-trace">
            {ticket.trace.map((s, i) => (
              <div className="admin-trace-row" key={s.stage}>
                <div className="admin-trace-rail">
                  <div className="admin-trace-node">{s.stage}</div>
                  {i < ticket.trace.length - 1 && <div className="admin-trace-line" />}
                </div>

                <div className="admin-trace-content">
                  <div className="admin-trace-text">{s.text}</div>
                  {s.meta && <div className="admin-trace-meta">{s.meta}</div>}

                  {s.checks && (
                    <div className="admin-check-list">
                      {s.checks.map((c) => (
                        <div className="admin-check" key={c.label}>
                          <span
                            className="admin-check-mark"
                            style={{ color: c.ok ? "var(--status-ok)" : "var(--status-danger)" }}
                          >
                            {c.ok ? "✓" : "✗"}
                          </span>
                          <span className="admin-check-label">{c.label}:</span>
                          <span>{c.result}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {s.isFix && (
                    <div className="admin-trace-actions">
                      {!action && (
                        <>
                          <button type="button" className="btn btn-primary" onClick={() => setAction("approved")}>
                            Approve
                          </button>
                          <button type="button" className="btn btn-secondary" onClick={() => setAction("rejected")}>
                            Reject
                          </button>
                          <button type="button" className="btn btn-secondary" onClick={() => setAction("investigating")}>
                            Investigate manually
                          </button>
                        </>
                      )}
                      {action === "approved" && <span className="tag tag-accent">Fix approved &amp; applied</span>}
                      {action === "rejected" && <span className="tag tag-neutral">Fix rejected</span>}
                      {action === "investigating" && <span className="tag tag-outline">Sent to manual investigation</span>}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="card">
          <div className="card-kicker">Requester</div>
          <div className="card-title" style={{ fontSize: 15 }}>{ticket.requester}</div>
          <div className="hr" style={{ margin: "10px 0" }} />
          <div className="card-kicker">AI framework</div>
          <p style={{ fontSize: 12, opacity: 0.75, margin: "4px 0 0" }}>
            This trace was produced automatically by BuildBeta's triage agent.
            Approving a suggested fix applies it directly; rejecting routes the
            ticket back to the open queue with the hypothesis discarded.
          </p>
        </div>
      </div>
    </>
  );
};

export default TicketDetail;
